import { Tooltip } from '@mui/material';
import type { MouseEvent } from 'react';
import React, { useCallback, useMemo } from 'react';
import { SecuredButton, SecuredIconButton } from '../../auth/Security';
import { SIZES } from '../../constants/constants';
import type { BreakpointSize } from '../../hooks/breakpoints';
import { useIsWithinBreakpoints } from '../../hooks/useIsWithinBreakpoints';
import { H } from '../../Layout';
import type { SecuredActionType } from '../ActionType';
import { getActionColor } from './LayoutUtils';

const ICON_ONLY_TOOLBAR_BREAKPOINTS: BreakpointSize[] = ['xs', 's'];

type SidePanelToolbarProps = {
    securedActions?: SecuredActionType[];
};

export const SidePanelToolbar = ({ securedActions = [] }: SidePanelToolbarProps) => {
    const isIconOnlyToolbar = useIsWithinBreakpoints(ICON_ONLY_TOOLBAR_BREAKPOINTS);

    const handleActionClick = useCallback((action: SecuredActionType, event: MouseEvent<HTMLElement>) => {
        if (typeof action.onClick !== 'function' || action.disabled) {
            return;
        }

        action.onClick(event);
    }, []);

    const securedActionsElement = useMemo(
        () => (
            <>
                {securedActions
                    .filter((action) => !action.hidden)
                    .map((action, index) => {
                        const color = getActionColor(action);
                        const securityTarget = {
                            entityName: action.entityName,
                            entitySecurity: action.entitySecurity,
                            featureName: action.featureName
                        };
                        const commonProps = {
                            securityAction: action.securityAction,
                            securityTarget,
                            disabled: Boolean(action.disabled),
                            onClick: (event: MouseEvent<HTMLElement>) => handleActionClick(action, event),
                            disableRipple: true
                        };

                        return (
                            <Tooltip key={index} title={action.tooltip}>
                                <H>
                                    {isIconOnlyToolbar ? (
                                        <SecuredIconButton
                                            {...commonProps}
                                            aria-label={typeof action.label === 'string' ? action.label : undefined}
                                            sx={{
                                                p: 0,
                                                color: color,
                                                '&:hover': { backgroundColor: 'transparent' }
                                            }}
                                        >
                                            <action.icon fontSize={SIZES.small} sx={{ color }} />
                                        </SecuredIconButton>
                                    ) : (
                                        <SecuredButton
                                            {...commonProps}
                                            variant={'text'}
                                            startIcon={<action.icon fontSize={SIZES.small} sx={{ color }} />}
                                            sx={{
                                                color: color,
                                                p: 0,
                                                textTransform: 'none',
                                                whiteSpace: 'nowrap',
                                                '&:hover': { backgroundColor: 'transparent' }
                                            }}
                                        >
                                            {action.label}
                                        </SecuredButton>
                                    )}
                                </H>
                            </Tooltip>
                        );
                    })}
            </>
        ),
        [handleActionClick, isIconOnlyToolbar, securedActions]
    );

    return securedActionsElement;
};
